import { useCallback } from 'react';
import { useRouter, NextRouter } from 'next/router';
import { A, D, G, S } from '@mobily/ts-belt';
import { FreestyleSearchFilter } from '../../types/definitions/vendor/graphql-schema.d';
import { FilterFragment } from './getSearchResults';

// Utility for overriding an object's properties type
// Source: https://dev.to/vborodulin/ts-how-to-override-properties-with-type-intersection-554l
export type Override<T, NewObject> = Omit<T, keyof NewObject> & NewObject;

export const filterQueryParam = ['color', 'occasion'] as const;
export type FilterQueryParam = (typeof filterQueryParam)[number];
type ValidQueryParamFilter = Override<
  FreestyleSearchFilter,
  { id: FilterQueryParam }
>;

const isValidFilterQueryParam = (
  queryParam: string,
): queryParam is FilterQueryParam => {
  return new Set<string>(filterQueryParam).has(queryParam);
};

export const isValidFilter = (
  filter: FilterFragment,
): filter is ValidQueryParamFilter => {
  return isValidFilterQueryParam(filter.id);
};

export const sortQueryParam = 'sort';
export type SortQueryParam = typeof sortQueryParam;

export type SearchParams = {
  [Param in FilterQueryParam]?: string[];
} & {
  q?: string;
  sort?: string;
  page: number;
  autoSearch?: boolean;
};
export type SearchParam = keyof SearchParams;
type SearchParamValue = SearchParams[SearchParam];

const normalizeFilterQueryParam = (value: SearchParamValue) => {
  if (G.isString(value) && S.isNotEmpty(value)) {
    return [value];
  }
  if (G.isArray(value) && A.isNotEmpty(value)) {
    return value;
  }

  return undefined;
};

/**
 * Parses the `page` string query param into a number.
 * If page is not a valid number, returns `0` by default.
 */
const parsePageQueryParam = (page: string | string[] | undefined): number => {
  let validPage = 0;

  if (G.isUndefined(page)) {
    validPage = 1;
  }

  if (G.isString(page)) {
    const parsedPage = parseInt(page, 10);

    // Guard against `parseInt` returning `NaN` when page is an invalid number string, e.g., "foo", "-3", etc.
    if (G.isNumber(parsedPage) && parsedPage > 0 && parsedPage <= 10) {
      validPage = parsedPage;
    }
  }

  return validPage;
};

const getQueryParams = (query: NextRouter['query']) => {
  const { color, occasion, q, sort, page, auto_search } = query;
  const queryParams: SearchParams = {
    color: normalizeFilterQueryParam(color),
    occasion: normalizeFilterQueryParam(occasion),
    page: parsePageQueryParam(page),
  };

  if (G.isString(q) && S.isNotEmpty(q)) {
    queryParams.q = q;
  }
  if (G.isString(sort) && S.isNotEmpty(sort)) {
    queryParams.sort = sort;
  }
  if (G.isString(auto_search) && S.isNotEmpty(auto_search)) {
    queryParams.autoSearch = auto_search === 'true';
  }

  return queryParams;
};

const removeEmptyQueryParams = (queryParams: NextRouter['query']) => {
  return D.filter(queryParams, value => {
    const isNotEmptyString = G.isString(value) && S.isNotEmpty(value);
    const isNotEmptyArray = G.isArray(value) && A.isNotEmpty(value);

    return isNotEmptyString || isNotEmptyArray;
  });
};

export interface UseQueryParamReturn {
  isReady: boolean;
  queryParams: SearchParams;
  reload: NextRouter['reload'];
  /**
   * Use `setQueryParam` when setting a single URL query param, excluding `page`.
   */
  setQueryParam: <T extends Exclude<SearchParam, 'page'>>(
    name: T,
    value: NonNullable<SearchParams[T]>,
  ) => void;
  /**
   * Use `setQueryParams` when setting **multiple** URL query params, excluding `q` and `page`.
   *
   * - Does not allow setting the `q` query param since we must clear _all_ existing query params;
   *   use `setQueryParam('q', newValue)` instead.
   * - The `page` query param is removed when setting new query params to reset the page to 1.
   */
  setQueryParams: (queryParams: Omit<SearchParams, 'q' | 'page'>) => void;
}

const updateQueryParams = (
  queryParams: Partial<SearchParams>,
  query: NextRouter['query'],
  push: NextRouter['push'],
) => {
  const mergedQueryParams = D.merge(query, queryParams);
  const newQueryParams = removeEmptyQueryParams(mergedQueryParams);

  delete newQueryParams.page;
  push(
    {
      query: newQueryParams,
    },
    undefined,
    { scroll: true, shallow: true },
  );
};

const handleSetQueryParam = (
  name: SearchParam,
  value: NonNullable<SearchParamValue>,
  query: NextRouter['query'],
  push: NextRouter['push'],
) => {
  if (name === 'q' && G.isString(value) && S.isNotEmpty(value)) {
    /**
     * a new query resets filters, sort, and page
     */
    push({ query: { q: value } }, undefined, { scroll: true, shallow: true });
  } else {
    updateQueryParams({ [name]: value }, query, push);
  }
};

/**
 * the `useSearchParams` hook filters the `next/router` query object
 * down to only the applicable keys and types for the search page.
 *
 * Provides the `setQueryParam` and `setQueryParams` helpers to add or update URL search params.
 * Provides the `getPageHrefQueryParams` helper to generate the `href.query` prop of a Next `Link` component for paginating.
 */
export const useSearchParams = (): UseQueryParamReturn => {
  const { isReady, query, push, reload } = useRouter();

  const setQueryParam = useCallback<UseQueryParamReturn['setQueryParam']>(
    (name, value) => handleSetQueryParam(name, value, query, push),
    [query, push],
  );

  const setQueryParams = useCallback<UseQueryParamReturn['setQueryParams']>(
    queryParams => updateQueryParams(queryParams, query, push),
    [query, push],
  );

  return {
    isReady,
    queryParams: getQueryParams(query),
    reload,
    setQueryParam,
    setQueryParams,
  };
};
